
import React, { useState, lazy, Suspense, startTransition, useEffect } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './components/ui/Sidebar';
import { Footer } from './components/ui/Footer';
import { AppProvider, useApp } from './services/store';
import { UserRole } from './types';
import { ChangelogModal } from './components/modules/ChangelogModal';
import { Menu, Loader2, AlertTriangle } from 'lucide-react';

// Lazy load heavy components
const SettingsView = lazy(() => import('./components/admin/SettingsView.tsx').then(module => ({ default: module.SettingsView })));
const PlaylistEditor = lazy(() => import('./components/views/PlaylistEditor.tsx').then(module => ({ default: module.PlaylistEditor })));
const LoginView = lazy(() => import('./components/user/LoginView.tsx').then(module => ({ default: module.LoginView })));
const PasswordResetView = lazy(() => import('./components/user/PasswordResetView.tsx').then(module => ({ default: module.PasswordResetView })));
const CloudFilesView = lazy(() => import('./components/views/CloudFilesView').then(module => ({ default: module.CloudFilesView })));
const DynamicCloudFilesView = lazy(() => import('./components/views/DynamicCloudFilesView').then(module => ({ default: module.DynamicCloudFilesView })));
const WorshipView = lazy(() => import('./components/views/WorshipView').then(module => ({ default: module.WorshipView })));
const PlaylistPlayer = lazy(() => import('./components/views/PlaylistPlayer.tsx').then(module => ({ default: module.PlaylistPlayer })));
const UserManagement = lazy(() => import('./components/user/UserManagement.tsx').then(module => ({ default: module.UserManagement })));
const ChurchesView = lazy(() => import('./components/admin/ChurchesView.tsx').then(module => ({ default: module.ChurchesView })));
const ChangelogView = lazy(() => import('./components/admin/ChangelogView.tsx').then(module => ({ default: module.ChangelogView })));
const LogViewer = lazy(() => import('./components/admin/LogViewer.tsx').then(module => ({ default: module.LogViewer })));
const DashboardView = lazy(() => import('./components/views/DashboardView').then(module => ({ default: module.DashboardView })));
const PlaylistListView = lazy(() => import('./components/views/PlaylistListView').then(module => ({ default: module.PlaylistListView })));
const LandingPage = lazy(() => import('./components/views/LandingPage'));
const ImpressumView = lazy(() => import('./components/datenschutz/ImpressumView'));
const DatenschutzView = lazy(() => import('./components/datenschutz/DatenschutzView'));

// --- ERROR BOUNDARY ---
interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren<{}>, ErrorBoundaryState> {
    constructor(props: React.PropsWithChildren<{}>) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    override render() {
        if (this.state.hasError) {
            return (
                <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center p-8">
                        <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Something went wrong
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            An error occurred while loading the application.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// --- AUTH GUARD ---
const RequireAuth = ({ children }: { children: React.ReactElement }) => {
    const { user, isInitialized } = useApp();
    const location = useLocation();

    // Show loading while app is initializing
    if (!isInitialized) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader2 size={48} className="animate-spin text-brand-600" />
            </div>
        );
    }

    if (!user) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience.
        startTransition(() => {
            // Use startTransition to avoid synchronous navigation errors
        });
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
};

// --- PUBLIC PAGE GUARD ---
const PublicPage = ({ children }: { children: React.ReactElement }) => {
    const { user, isInitialized } = useApp();
    
    // Show loading while app is initializing
    if (!isInitialized) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader2 size={48} className="animate-spin text-brand-600" />
            </div>
        );
    }
    
    // If user is logged in, redirect to dashboard
    if (user) {
        startTransition(() => {
            // Use startTransition to avoid synchronous navigation errors
        });
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

// --- ROLE GUARD ---
const RequireRole = ({ children, roles }: { children: React.ReactElement, roles: UserRole[] }) => {
    const { user, isInitialized } = useApp();
    
    // Show loading while app is initializing
    if (!isInitialized) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader2 size={48} className="animate-spin text-brand-600" />
            </div>
        );
    }
    
    if (!user || !roles.includes(user.role)) {
        startTransition(() => {
            // Use startTransition to avoid synchronous navigation errors
        });
        return <Navigate to="/" replace />;
    }
    return children;
}

// --- LAYOUT COMPONENT ---
const Layout: React.FC = () => {
    const { needsChangelog, submitChangelog, currentVersion, user } = useApp();
    const { theme } = useApp();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <div className={`${theme}`}>
            <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">

                {/* Desktop Sidebar */}
                <div className="hidden lg:block fixed left-0 top-0 h-screen z-40">
                    <Sidebar
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                    />
                </div>

                {/* Mobile Sidebar Overlay */}
                {isMobileMenuOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden flex">
                        <div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                        <div className="relative w-64 h-full bg-white dark:bg-gray-900 shadow-xl">
                            <Sidebar
                                isCollapsed={false}
                                setIsCollapsed={setIsSidebarCollapsed}
                                onCloseMobile={() => setIsMobileMenuOpen(false)}
                            />
                        </div>
                    </div>
                )}

                <main className={`flex-1 flex flex-col min-w-0 overflow-hidden ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                    {/* Mobile Header */}
                    <div className="lg:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between sticky top-0 z-30 shadow-sm">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white font-bold">CC</div>
                            <span className="font-bold text-lg text-gray-900 dark:text-white">ChurchCast</span>
                        </div>
                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
                        >
                            <Menu size={24} />
                        </button>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 overflow-y-auto pb-8">
                        <Outlet />
                    </div>

                    {/* Footer - Fixed at bottom */}
                    <Footer isCollapsed={isSidebarCollapsed} />
                </main>
                {user?.role === UserRole.SUPER_ADMIN && (
                    <ChangelogModal
                        isOpen={needsChangelog}
                        onClose={() => {}}
                        onSubmit={submitChangelog}
                        version={currentVersion}
                    />
                )}
            </div>
        </div>
    );
};

const AppRoutes = () => {
    const { isInitialized } = useApp();
    const navigate = useNavigate();

    if (!isInitialized) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader2 size={48} className="animate-spin text-brand-600" />
            </div>
        );
    }

    return (
        <Suspense fallback={
            <div className="h-screen w-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <Loader2 size={48} className="animate-spin text-brand-600" />
            </div>
        }>
            <Routes>
                {/* Public Routes - Only accessible when NOT logged in */}
                <Route path="/" element={
                    <PublicPage>
                        <LandingPage />
                    </PublicPage>
                } />
                <Route path="/login" element={<LoginView />} />
                <Route path="/reset-password" element={<PasswordResetView />} />
                <Route path="/impressum" element={<ImpressumView />} />
                <Route path="/datenschutz" element={<DatenschutzView />} />

                {/* Presentation View (Outside Layout) */}
                <Route path="/playlists/present/:id" element={
                    <RequireAuth>
                        <PlaylistPlayer />
                    </RequireAuth>
                } />

                {/* Main App Layout - Only accessible when logged in */}
                <Route element={
                    <RequireAuth>
                        <Layout />
                    </RequireAuth>
                }>
                    <Route path="/dashboard" element={<DashboardView />} />
                    <Route path="/playlists" element={<PlaylistListView />} />

                    <Route path="/playlists/new" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN, UserRole.EDITOR]}>
                            <PlaylistEditor />
                        </RequireRole>
                    } />

                    <Route path="/playlists/edit/:id" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN, UserRole.EDITOR]}>
                            <PlaylistEditor />
                        </RequireRole>
                    } />

                    <Route path="/material" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN, UserRole.EDITOR]}>
                            <DynamicCloudFilesView />
                        </RequireRole>
                    } />
                    
                    <Route path="/material/:folderIndex" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN, UserRole.EDITOR]}>
                            <DynamicCloudFilesView />
                        </RequireRole>
                    } />

                    <Route path="/lobpreis" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN, UserRole.EDITOR]}>
                            <WorshipView />
                        </RequireRole>
                    } />

                    <Route path="/benutzer" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN]}>
                            <UserManagement />
                        </RequireRole>
                    } />

                    <Route path="/einstellungen" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN]}>
                            <SettingsView />
                        </RequireRole>
                    } />

                    <Route path="/kirchen" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN]}>
                            <ChurchesView />
                        </RequireRole>
                    } />

                    <Route path="/changelogs" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN, UserRole.CHURCH_ADMIN]}>
                            <ChangelogView />
                        </RequireRole>
                    } />

                    <Route path="/logs" element={
                        <RequireRole roles={[UserRole.SUPER_ADMIN]}>
                            <LogViewer />
                        </RequireRole>
                    } />
                </Route>

                {/* Fallback - redirect based on auth status */}
                <Route path="*" element={
                    <RequireAuth>
                        <Navigate to="/dashboard" replace />
                    </RequireAuth>
                } />
            </Routes>
        </Suspense>
    );
}

const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <AppProvider>
                <AppRoutes />
            </AppProvider>
        </ErrorBoundary>
    );
};

export default App;
